<template>
  <section v-if="isSonarPageReady" class="sonar">
    <div class="sonar__container">
      <div v-if="!isSonarQuizRunning" class="sonar__header">
        <div
          :class="[
            'sonar__header-logo-wrapper',
            !showUserStats && 'sonar__header-logo-wrapper-empty',
          ]"
        >
          <img
            src="../assets/images/sonar-logo.png"
            alt="Sonar logo"
            class="sonar__header-logo"
          />
        </div>
      </div>
      <SonarOverviewCard
        v-if="showOverviewCard"
        class="sonar__overview-card"
        :currentScore="currentScore"
        :userHasStats="!!showUserStats"
      />
      <SonarWelcomeCard v-if="showWelcomeCard" />

      <SonarAssessments
        :assessments="assessments"
        :userHasStats="!showUserStats || comingSoon"
        :isEnabled="!comingSoon"
      />
      <div class="sonar__coming-soon-wrapper">
        <SonarComingSoon class="sonar__coming-soon-img" />
        <div class="sonar__coming-soon-title">
          {{ comingSoonTitle }}
        </div>
        <div class="sonar__coming-soon-info" v-html="comingSoonInfo" />
      </div>
      <router-view :key="$route.fullPath" />
    </div>
  </section>
  <div v-else class="sonar__loader-wrapper">
    <ActionLoader />
  </div>
</template>
<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import SonarOverviewCard from '@/sonar/components/SonarOverviewCard.vue'
import SonarWelcomeCard from '@/sonar/components/SonarWelcomeCard.vue'
import SonarAssessments from '@/sonar/components/SonarAssessments.vue'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import SonarComingSoon from '../assets/images/coming-soon.svg'
import { isEmpty } from 'lodash'
import CONSTANTS from '@/constants'
import { getSonarAssessmentValue } from '@/sonar/helpers/sessionStorageHandler'

export default {
  name: 'SonarPage',
  components: {
    SonarWelcomeCard,
    SonarOverviewCard,
    SonarAssessments,
    ActionLoader,
    SonarComingSoon,
  },
  data() {
    return {
      comingSoon: false,
    }
  },
  computed: {
    ...mapState({
      isPageLoading: state => state.app.isPageLoading,
      isAuthenticated: state => state.auth.isAuthenticated,
    }),
    ...mapGetters({
      assessments: 'sonar/assessments',
      currentScore: 'sonar/currentScore',
      displaySonar: 'sonar/displaySonar',
      isSonarQuizRunning: 'sonar/isSonarQuizRunning',
      lastActiveLearningArea: 'sonar/lastActiveLearningArea',
      currentLearningArea: 'sonar/currentLearningArea',
    }),
    isSonarPageReady() {
      return this.displaySonar && !this.isPageLoading
    },
    learningArea() {
      const learning_area_slug =
        this.$route.params?.learningArea ||
        getSonarAssessmentValue('learning_area')?.slug
      return (
        Object.values(CONSTANTS.SONAR.LEARNING_AREAS).find(
          la => la.slug === learning_area_slug
        ) || CONSTANTS.SONAR.LEARNING_AREAS['Cyber Security']
      )
    },
    showUserStats() {
      return this.currentScore?.user_status?.groups.length
    },
    showOverviewCard() {
      return this.showUserStats && !this.isSonarQuizRunning
    },
    showWelcomeCard() {
      return !this.showUserStats && !this.isSonarQuizRunning
    },
    comingSoonAvailable() {
      return Object.values(CONSTANTS.SONAR.LEARNING_AREAS)
        .filter(la => la.name !== 'Cyber Security')
        .map(la => la.name)
    },
    comingSoonTitle() {
      if (
        this.currentLearningArea.name ===
        CONSTANTS.SONAR.LEARNING_AREAS.Cloud.name
      ) {
        return 'Cloud skills assessments coming in early 2024'
      }
      return this.comingSoonAvailable.includes(this.currentLearningArea.name)
        ? `${this.currentLearningArea.name} skill assessments coming late summer 2023!`
        : 'New Sonar skill assessments coming soon!'
    },
    comingSoonInfo() {
      return this.comingSoonAvailable.includes(this.currentLearningArea.name)
        ? `We're adding new assessments in <span class='sonar__coming-soon--${this.currentLearningArea.slug}'>${this.currentLearningArea.name}</span>. Stay tuned for the latest updates!`
        : "We're adding new assessments in cloud, networking, and cyber security. Stay tuned for the latest updates!"
    },
  },
  watch: {
    $route: {
      handler: async function(to, from) {
        const isSwitchingTabs =
          this.$route?.name === 'sonar' &&
          !!from?.params?.learningArea &&
          from?.params?.learningArea !== this.$route?.params?.learningArea
        if (isSwitchingTabs) await this.queryData()
        this.$store.dispatch('app/setIsPageLoading', false)
      },
      deep: true,
      immediate: true,
    },
  },
  async beforeMount() {
    if (this.displaySonar) {
      await this.queryData()
      return this.$store.dispatch('app/setIsPageLoading', false)
    }
    this.$router.push({ name: 'home' })
  },

  methods: {
    ...mapMutations({
      setSonarStatus: 'sonar/CURRENT_SONAR_STATUS_UPDATED',
    }),
    async queryData() {
      if (!this.isAuthenticated || (this.isAuthenticated && !this.displaySonar))
        return
      this.$store.dispatch('app/setIsPageLoading', true)
      const currentLearningArea = this.learningArea
      this.setSonarStatus({ currentLearningArea })
      this.comingSoon = currentLearningArea?.disabled
      if (this.comingSoon)
        return this.$store.dispatch('app/setIsPageLoading', false)
      const learningAreaId =
        currentLearningArea?.id ||
        CONSTANTS.SONAR.LEARNING_AREAS['Cyber Security'].id
      const promises = []

      if (isEmpty(this.currentScore)) {
        promises.push(
          this.$store.dispatch(
            'sonar/getUserScoreByLearningArea',
            learningAreaId
          )
        )
      }
      if (!this.assessments?.length)
        promises.push(
          this.$store.dispatch('sonar/getAssessments', learningAreaId)
        )
      await Promise.allSettled(promises)
    },
  },
}
</script>
